import {
  type FetchKPIs_me_mmm_availableKpis_kpis,
  type FetchBlessedBudgetMixesForCycle_me_mmm_cycle_clientSettings,
  type FetchCycleDataColumnsV2_me_mmm_availableDataColumns_dataColumns,
} from "@north-beam/nb-client-side";

export type ClientSettingDateRange = {
  id: number;
  start_date: string;
  end_date: string;
};

export type MixValidation = {
  id: number;
  feature: {
    id: number;
    column_name: string;
    is_from_mta: boolean;
    mta_breakdowns: {
      key: string;
      value: string;
    }[];
    friendly_name: string;
  };
  percentage_low: number;
  percentage_high: number;
};

export type SpendConstraintFeature = {
  id: string;
  column_name: string;
  is_from_mta: boolean;
  mta_breakdowns: {
    key: string;
    value: string;
  }[];
  friendly_name: string;
};

export type SpendConstraint = {
  id: number;
  budget_low: number | null;
  budget_high: number | null;
} & (
  | {
      feature: SpendConstraintFeature;
      feature_v2?: SpendConstraintFeature;
    }
  | { feature?: SpendConstraintFeature; feature_v2: SpendConstraintFeature }
);
export enum KpiType {
  REVENUE = "price_value",
  TRANSACTIONS = "transactions",
  CUSTOMERS = "first_time_customers",
}

export type ClientSetting = {
  id: string;
  client_setting_date_range: ClientSettingDateRange;
  client_id: string;
  spend_constraints: SpendConstraint[];
  createBudgetRecommendationRunResults: FetchBlessedBudgetMixesForCycle_me_mmm_cycle_clientSettings["createBudgetRecommendationRunResults"];
  reference_start_date?: string;
  reference_end_date?: string;
  kpi?: Kpi;
  kpi_v2?: Kpi;
  name: string;
  budget_low: number;
  budget_high: number;
  desired_kpi_value: number | null;
  target_spend: number;
  max_cost_per_unit_kpi: number | null;
  profit_value_per_unit_kpi: number | null;
  locked: boolean;
  cycle_id: string;
};

export type EmptyClientSetting = Omit<
  ClientSetting,
  | "id"
  | "budget_low"
  | "budget_high"
  | "activated_at"
  | "retired_at"
  | "kpi"
  | "kpi_v2"
  | "desired_kpi_value"
  | "target_spend"
> & {
  id?: string;
  budget_low?: number | null;
  budget_high?: number | null;
  kpi?: Kpi | null;
  kpi_v2?: Kpi | null;
  kpi_target?: number | null;
  desired_kpi_value?: number | null;
  target_spend?: number | null;
};

export type DataColumn = Omit<
  FetchCycleDataColumnsV2_me_mmm_availableDataColumns_dataColumns,
  "__typename" | "mta_breakdowns"
> & {
  mta_breakdowns?:
    | {
        key: string;
        value: string;
      }[]
    | null;
};

type Equation = {
  type: string;
  value: string;
  left?: Equation;
  right?: Equation;
};
export type Kpi = Omit<
  FetchKPIs_me_mmm_availableKpis_kpis,
  "__typename" | "equation" | "northbeam_client" | "kpi_type"
> & { equation: Equation; kpi_type: KpiType };

export type ModelMetadataDataColumn = {
  is_ready_for_use_in_model: boolean;
  column_name: string;
  is_present: boolean;
  is_from_mta: boolean;
  is_from_mmm_custom_data: boolean;
  is_spend: boolean;
  is_target: boolean;
  is_calendar_of_events: boolean;
  mta_breakdowns: { key: string; value: string }[] | null;
  friendly_name: string;
  column_type: string;
  target_typical_value: number | null;
  target_price_value_per_unit_target: number | null;
  dataset_most_recent_non_blank_date: string | null;
  approval_status: string;
  operator_notes: string;
};

export type ModelMetadataKpiFormula = {
  kpi_name: string;
  equation: any;
  friendly_name: string;
  is_active: boolean;
  is_default_kpi: boolean;
  kpi_type: KpiType;
};

export type CalendarOfEventsEvent = {
  description: string;
  campaign_type: string;
  start_date: string;
  end_date: string;
};

export type ModelMetadata = {
  hdi_prob: number;
  model_run_date: string;
  training_end_date: string;
  training_start_date: string;
  start_day_of_week: string;
  out_of_sample_cutoff_date: string;
  period_length_days: number;
  model_metrics: any;
  indicator_data_columns: ModelMetadataDataColumn[];
  promo_data_columns: ModelMetadataDataColumn[];
  spend_data_columns: ModelMetadataDataColumn[];
  target_data_columns: ModelMetadataDataColumn[];
  kpi_formulas: ModelMetadataKpiFormula[];
  coe: CalendarOfEventsEvent[];
};
